// src/resources/rdf/RdfList.js
import * as React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    SelectInput,
    FunctionField,
} from 'react-admin';
import ValidateButton from './ValidateButton';
import ValidateWithTermsButton from './ValidateWithTermsButton';
import RejectButton from './RejectButton';
import StatusField from '../../components/StatusField';
import ResourceStatusHeader from '../../components/ResourceStatusHeader';
import { useResourceSearchFilter } from '../../hooks/useResourceSearch';

const RdfFilters = [
    <SelectInput
        source="status"
        label="Statut"
        alwaysOn
        choices={[
            { id: 'PENDING', name: 'PENDING' },
            { id: 'VALIDATED', name: 'VALIDATED' },
            { id: 'REJECTED', name: 'REJECTED' },
        ]}
    />,
];

const describeVariantType = (typeLabel, typeCode) => typeLabel || typeCode || null;

const stripTrailingTypeTag = (label, usesVariant) => {
    if (!label) return label;
    if (!usesVariant) return label;
    const cleaned = label.replace(/\s*\([^)]*\)\s*$/, '').trim();
    return cleaned.length ? cleaned : label;
};

const formatTermWithNature = ({
    base,
    typeLabel,
    typeCode,
    usesVariant,
    baseStatus,
    variantStatus,
}) => {
    const normalizedBase = stripTrailingTypeTag(base, usesVariant);
    const value = normalizedBase ?? '—';
    if (usesVariant === undefined || usesVariant === null) {
        return value;
    }

    const status = usesVariant ? variantStatus || baseStatus : baseStatus;
    const statusSegment = status ? `Statut : ${status}` : null;

    if (!usesVariant) {
        return statusSegment ? `${value} (Nu · ${statusSegment})` : `${value} (Nu)`;
    }

    const variantType = describeVariantType(typeLabel, typeCode);
    const suffixParts = [`Variante${variantType ? ` : ${variantType}` : ''}`];
    if (statusSegment) {
        suffixParts.push(statusSegment);
    }
    return `${value} (${suffixParts.join(' · ')})`;
};

const renderSubjectCell = (record) => {
    if (!record) return '—';
    return formatTermWithNature({
        base: record.subject,
        typeLabel: record.subjectVariantTypeLabel,
        typeCode: record.subjectVariantType,
        usesVariant: record.subjectUsesVariant,
        baseStatus: record.subjectStatus,
        variantStatus: record.subjectVariantStatus,
    });
};

const renderOobjectCell = (record) => {
    if (!record) return '—';
    return formatTermWithNature({
        base: record.oobject,
        typeLabel: record.oobjectVariantTypeLabel,
        typeCode: record.oobjectVariantType,
        usesVariant: record.oobjectUsesVariant,
        baseStatus: record.oobjectStatus,
        variantStatus: record.oobjectVariantStatus,
    });
};

export default function RdfList() {
    const searchFilter = useResourceSearchFilter('rdf');

    return (
        <>
            <ResourceStatusHeader resource="rdf" title="RDF" subtitle="Totaux par statut" />
            <List
                exporter={false}
                perPage={25}
                sort={{ field: 'createdAt', order: 'DESC' }}
                filters={RdfFilters}
                filter={searchFilter}
                title="RDF"
            >
                <Datagrid rowClick="show" bulkActionButtons={false}>
                    <TextField source="id" />
                    <TextField source="witWik" />
                    <FunctionField
                        source="subject"
                        label="Subject"
                        sortBy="subject"
                        render={renderSubjectCell}
                    />
                    <TextField source="predicate" label="Predicate" sortBy="predicate" />
                    <FunctionField
                        source="oobject"
                        label="Object"
                        sortBy="oobject"
                        render={renderOobjectCell}
                    />
                    <TextField source="username" label="Auteur" />
                    <StatusField source="status" />
                    <DateField source="createdAt" showTime />
                    <ValidateButton />
                    <ValidateWithTermsButton />
                    <RejectButton />
                </Datagrid>
            </List>
        </>
    );
}
